// List + detail in one file because the test rig has no react-router.
// When this moves into the work repo, `selectedCode` becomes a URL param
// and the two modes split into `/warehouse/trips` and `/warehouse/trips/:code`.

import React, { useMemo, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
  Nav,
} from 'react-bootstrap';

import { useTrips } from '../../features/warehouse/useTrips.js';
import { tripStatusColor } from '../../features/warehouse/warehouseUtils.js';
import CreateTripModal from '../../components/warehouse/CreateTripModal.js';
import TripDetailPage from './TripDetailPage.js';

const STATUS_TABS = ['active', 'all', 'draft', 'loading', 'loaded', 'shipped'];

export default function TripsPage({ onOpenTruckView, onOpenPickRoute, onOpenCmr }) {
  const [selectedCode, setSelectedCode] = useState(null);
  const [tab, setTab] = useState('active');
  const [createOpen, setCreateOpen] = useState(false);

  const { data: trips, isLoading, error } = useTrips();

  const visible = useMemo(() => {
    if (!trips) return [];
    if (tab === 'all') return trips;
    if (tab === 'active') {
      return trips.filter(t => t.status === 'draft' || t.status === 'loading' || t.status === 'loaded');
    }
    return trips.filter(t => t.status === tab);
  }, [trips, tab]);

  // Detail mode — rendered in place of the list, with a back button.
  if (selectedCode) {
    return (
      <TripDetailPage
        code={selectedCode}
        onBack={() => setSelectedCode(null)}
        onOpenTruckView={onOpenTruckView}
        onOpenPickRoute={onOpenPickRoute}
        onOpenCmr={onOpenCmr}
      />
    );
  }

  return (
    <Container fluid className="py-3">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">Shipment trips</h4>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            + New trip
          </Button>
        </Col>
      </Row>

      <Nav variant="tabs" activeKey={tab} onSelect={k => setTab(k)} className="mb-2">
        {STATUS_TABS.map(s => (
          <Nav.Item key={s}>
            <Nav.Link eventKey={s} className="text-capitalize">
              {s}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      <Card>
        <Card.Body className="p-0">
          {isLoading && (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          )}
          {error && (
            <Alert variant="danger" className="m-3">
              {error.message}
            </Alert>
          )}

          {!isLoading && visible.length === 0 && (
            <div className="text-center text-muted py-5">
              No trips in this tab. Create one to start loading items.
            </div>
          )}

          {visible.length > 0 && (
            <Table hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Plate</th>
                  <th>Driver</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th className="text-end">Items</th>
                  <th className="text-end">Length</th>
                  <th className="text-end">Weight</th>
                  <th>Planned</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(trip => (
                  <tr
                    key={trip._id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedCode(trip.code)}
                  >
                    <td><strong>{trip.code}</strong></td>
                    <td>{trip.plate || <span className="text-muted">—</span>}</td>
                    <td>{trip.driver || <span className="text-muted">—</span>}</td>
                    <td>{trip.customer || <span className="text-muted">—</span>}</td>
                    <td>
                      <Badge bg={tripStatusColor(trip.status)}>
                        {trip.status}
                      </Badge>
                    </td>
                    <td className="text-end">{trip.items?.length || 0}</td>
                    <td className="text-end">{trip.totalLengthM || 0} m</td>
                    <td className="text-end">{trip.totalWeightKg || 0} kg</td>
                    <td className="small text-muted">
                      {trip.plannedDeparture
                        ? new Date(trip.plannedDeparture).toLocaleString()
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <CreateTripModal
        show={createOpen}
        onHide={() => setCreateOpen(false)}
        onCreated={(trip) => setSelectedCode(trip.code)}
      />
    </Container>
  );
}
